import React, { useEffect } from "react";
import BaseButton from "@reusables/BaseButton";
import BaseInput from "@reusables/BaseInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BasePhoneInput from "@reusables/BasePhoneInput";
import BaseModal from "@reusables/Modals/BaseModal";
import { toastError } from "@helpers/utils";
import { useCreateCustomerContactMutation } from "@redux/features/customers/customersApi";
import { Customer } from "@/types/general";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import { useTranslation } from "react-i18next";

type CreateTheirReferenceModalProps = {
    customer?: Customer.Extended;
    isOpen: boolean;
    onClose: () => void;
    onCreate: (contact: { id: number, name: string, phone?: string, email?: string }) => void;
}

const formScheme = z.object({
    name: z.string().min(2),
    phone: z.string().optional(),
    email: z.string().email().optional()
});

type FormTyping = z.infer<typeof formScheme>

export default function CreateTheirReferenceModal({
                                                      customer,
                                                      isOpen,
                                                      onClose,
                                                      onCreate
                                                  }: CreateTheirReferenceModalProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.dropdowns.theirReference.creationModal" });

    const [createContact, { isLoading: isCreationLoading }] = useCreateCustomerContactMutation();

    const { control, handleSubmit, reset } = useForm<FormTyping>({
        resolver: zodResolver(formScheme)
    });

    useEffect(() => {
        if (isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const onSubmit = handleSubmit((data) => {
        if (!customer) {
            toast.error("Customer is not defined");
            return;
        }


        createContact({
            customer_id: customer.id,
            name: data.name,
            phone: data.phone,
            email: data.email
        }).unwrap()
            .then((data) => {
                onCreate(data);
            })
            .catch((e) => {
                toastError(e);
            });
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={562}
            padding="56px"
            useCloseIcon
            isLoading={isCreationLoading}
        >
            <form className="space-y-8" onSubmit={onSubmit}>
                <div className="modal-title">{t("heading")}</div>

                <BaseInputsGrid cols={1}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.name")}
                            />
                        )}
                    />

                    <Controller
                        name="phone"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BasePhoneInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.phone")}
                                variant="custom"
                            />
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.email")}
                            />
                        )}
                    />
                </BaseInputsGrid>

                <BaseButton
                    buttonWidth="100%"
                    text={t("buttons.create")}
                    size="md"
                    type={"submit"}
                />

            </form>
        </BaseModal>
    );
}
